import React, { useState, useEffect } from 'react';
import { Link2, ExternalLink, FileText, Code, ChevronRight, Sparkles } from 'lucide-react';
import api from '../utils/api';

const CustomDashboardTabs = () => {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTab, setExpandedTab] = useState(null);

  useEffect(() => {
    fetchTabs();
  }, []);

  const fetchTabs = async () => {
    try {
      const res = await api.get('/tabs/my-tabs');
      setTabs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch custom tabs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLinkIcon = (type) => {
    switch (type) {
      case 'file':
        return <FileText className="w-4 h-4" />;
      case 'embed':
        return <Code className="w-4 h-4" />;
      default:
        return <ExternalLink className="w-4 h-4" />;
    }
  };

  if (loading) {
    return null;
  }

  if (tabs.length === 0) {
    return null; // Don't show section if no tabs
  }

  return (
    <div className="mt-6" data-testid="custom-tabs-section">
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-muted-foreground">
        <Sparkles className="w-5 h-5 text-yellow-500" />
        Quick Links
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="glass-card overflow-hidden hover:border-primary/50 transition-all"
            data-testid={`custom-tab-${tab.id}`}
          >
            {/* Tab Header */}
            <button
              onClick={() => setExpandedTab(expandedTab === tab.id ? null : tab.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">{tab.title}</h3>
                  {tab.description && (
                    <p className="text-xs text-muted-foreground">{tab.description}</p>
                  )}
                </div>
              </div>
              <ChevronRight 
                className={`w-5 h-5 text-muted-foreground transition-transform ${
                  expandedTab === tab.id ? 'rotate-90' : ''
                }`} 
              />
            </button>

            {/* Expanded Content */}
            {expandedTab === tab.id && (
              <div className="px-4 pb-4 space-y-2 border-t border-white/10 pt-3">
                {/* Links */}
                {tab.links && tab.links.length > 0 && (
                  <div className="space-y-2">
                    {tab.links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all group"
                      >
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20">
                          {getLinkIcon(link.type)}
                        </div>
                        <span className="flex-1 font-medium text-sm">{link.title}</span>
                        <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
                      </a>
                    ))}
                  </div>
                )}

                {/* Embed Code */}
                {tab.embed_code && (
                  <div 
                    className="mt-3 rounded-xl overflow-hidden bg-black/20"
                    dangerouslySetInnerHTML={{ __html: tab.embed_code }}
                  />
                )}

                {/* No content message */}
                {(!tab.links || tab.links.length === 0) && !tab.embed_code && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No content available
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomDashboardTabs;
